import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import {
  Blocks,
  ChevronLeft,
  ChevronRight,
  ChevronsLeft,
  ChevronsRight,
  Layers3,
  Loader2,
  Search,
} from "lucide-react";
import { api, apiError } from "@/lib/api";
import { useProject } from "@/pages/project/ProjectLayout";
import { useProjectSocket } from "@/hooks/useProjectSocket";
import { useIsMobile } from "@/hooks/useIsMobile";
import { Input } from "@/components/ui/input";
import { BlockInspectorViewer } from "@/viewer-core/BlockInspectorViewer";
import { loadDefinition, loadInspectorManifest, clearInspectorCache } from "@/viewer-core/inspectorLoader";
import { NestedBlockTree } from "@/components/properties/NestedBlockTree";
import { BlockQrPanel } from "@/components/properties/BlockQrPanel";
import { BulkQrDialog } from "@/components/properties/BulkQrDialog";
import { InspectorToolbar } from "@/components/properties/InspectorToolbar";

function countNodes(nodes) {
  return (nodes || []).reduce(
    (sum, node) => sum + node.count + countNodes(node.children),
    0
  );
}

function collectPaths(nodes, out = []) {
  (nodes || []).forEach((node) => {
    out.push(node.path);
    collectPaths(node.children, out);
  });
  return out;
}

export default function PanelPropertiesPage() {
  const { projectId, project } = useProject();
  const [searchParams, setSearchParams] = useSearchParams();
  const containerRef = useRef(null);
  const viewerRef = useRef(null);

  const [manifest, setManifest] = useState(null);
  const [counts, setCounts] = useState({});
  const [query, setQuery] = useState("");
  const [defCategory, setDefCategory] = useState("panels");
  const [qrBulkOpen, setQrBulkOpen] = useState(false);
  const [activeName, setActiveName] = useState(null);
  const [tree, setTree] = useState([]);
  const [dims, setDims] = useState(null);
  const [partCount, setPartCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [hidden, setHidden] = useState(new Set());
  const [activeNode, setActiveNode] = useState(null);
  const [mode, setMode] = useState("orbit");
  const [sectionMode, setSectionMode] = useState(null); // null | 'x' | 'y' | 'z' | 'box'
  const [sectionPos, setSectionPos] = useState(0.5);
  const [measureTool, setMeasureTool] = useState("distance");
  const [measureState, setMeasureState] = useState({
    count: 0,
    selectedId: null,
    selectedText: null,
    pending: 0,
  });
  const [measureSettings, setMeasureSettings] = useState(() => {
    try {
      return {
        unit: "mm",
        decimals: 1,
        ...JSON.parse(localStorage.getItem("okn-measure-settings-v1") || "{}"),
      };
    } catch {
      return { unit: "mm", decimals: 1 };
    }
  });
  const [opacity, setOpacity] = useState(100);
  const [explode, setExplode] = useState(0);
  // Both side panels start collapsed on phones/tablets so the 3D canvas gets the
  // full height; on desktop they stay open as before.
  const { isCompact } = useIsMobile();
  const [leftOpen, setLeftOpen] = useState(() => !isCompact);
  const [rightOpen, setRightOpen] = useState(() => !isCompact);
  const scannedBlock = searchParams.get("block");

  // Panel instance counts per definition come from the block-definitions API.
  const loadCounts = useCallback(async () => {
    try {
      const { data } = await api.get(`/projects/${projectId}/block-definitions`);
      const map = {};
      (data.definitions || []).forEach((d) => {
        map[d.name] = d.panel_count;
      });
      setCounts(map);
    } catch {
      /* counts are supplementary */
    }
  }, [projectId]);

  useEffect(() => {
    loadCounts();
  }, [loadCounts]);

  useProjectSocket(projectId, (message) => {
    if (["block_definition_changed", "block_definitions_refreshed"].includes(message.event))
      loadCounts();
  });

  const revisionId = project?.active_revision_id;

  useEffect(() => {
    if (!revisionId) return;
    let alive = true;
    loadInspectorManifest(projectId, revisionId)
      .then((data) => alive && setManifest(data))
      .catch((err) => alive && setError(err.message));
    return () => {
      alive = false;
    };
  }, [projectId, revisionId]);

  // Panels carry a P<digits> code (P12, 1CC_P07, MTS_P1) or a P- prefix (P-RAY…),
  // anchors an ANC token (ANC-…, MTS_ANC_001); everything else falls into Others.
  const categoryOf = (name) => {
    if (/(^|[^a-z0-9])p(\d|-)/i.test(name)) return "panels";
    if (/(^|[^a-z0-9])anc/i.test(name)) return "anchors";
    return "others";
  };

  const definitions = useMemo(() => {
    const list = [...(manifest?.definitions || [])].sort((a, b) =>
      a.name.localeCompare(b.name)
    );
    const needle = query.trim().toLowerCase();
    return list.filter(
      (d) =>
        categoryOf(d.name) === defCategory &&
        (!needle || d.name.toLowerCase().includes(needle))
    );
  }, [manifest, query, defCategory]);

  const selectCategory = (id) => {
    setDefCategory(id);
    const list = [...(manifest?.definitions || [])]
      .sort((a, b) => a.name.localeCompare(b.name))
      .filter((d) => categoryOf(d.name) === id);
    if (list.length && !list.some((d) => d.name === activeName)) {
      setActiveName(list[0].name);
    }
  };

  const activeDefinition = useMemo(
    () => (manifest?.definitions || []).find((d) => d.name === activeName) || null,
    [manifest, activeName]
  );

  // Panels-category definitions for the bulk QR archive.
  const panelQrItems = useMemo(
    () =>
      [...(manifest?.definitions || [])]
        .filter((d) => categoryOf(d.name) === "panels")
        .sort((a, b) => a.name.localeCompare(b.name))
        .map((d) => ({ name: d.name, count: counts[d.name] ?? 0 })),
    [manifest, counts]
  );

  // First definition (or the QR-scanned one) opens automatically — prefer a
  // Panels-category definition so the QR panel is meaningful on first load.
  useEffect(() => {
    if (activeName || !manifest?.definitions?.length) return;
    const scanned = scannedBlock
      ? manifest.definitions.find(
          (d) => d.name.toLowerCase() === scannedBlock.toLowerCase()
        )
      : null;
    const sorted = [...manifest.definitions].sort((a, b) =>
      a.name.localeCompare(b.name)
    );
    const firstPanel = sorted.find((d) => categoryOf(d.name) === "panels");
    setActiveName(scanned?.name || (firstPanel || sorted[0]).name);
    if (scanned) setSearchParams({});
  }, [manifest, activeName, scannedBlock, setSearchParams]);

  // Viewer lifecycle: created once the container is mounted (needs revisionId).
  useEffect(() => {
    if (!containerRef.current || viewerRef.current) return undefined;
    const viewer = new BlockInspectorViewer(containerRef.current);
    viewer.onMeasureChange = (state) => setMeasureState(state);
    viewer.onMeasureNotice = (kind) => {
      if (kind === "laser-invalid")
        toast.info("Surface boundaries not detected — no measurement created");
    };
    window.__oknInspector = viewer; // diagnostics/testing handle
    viewer.onSelect = (hit) => setActiveNode(hit?.node || null);
    viewerRef.current = viewer;
    return () => {
      viewer.dispose();
      viewerRef.current = null;
      // Release cached definition geometries held on the GPU.
      clearInspectorCache();
    };
  }, [revisionId]);

  // Load the selected definition's geometry into the viewer (cached per type).
  useEffect(() => {
    if (!activeDefinition || !revisionId || !viewerRef.current) return;
    let alive = true;
    setLoading(true);
    setActiveNode(null);
    setHidden(new Set());
    setExplode(0);
    setSectionMode(null);
    loadDefinition(projectId, revisionId, activeDefinition)
      .then(({ parts, tree: nodeTree }) => {
        if (!alive || !viewerRef.current) return;
        viewerRef.current.setParts(parts);
        const size = viewerRef.current.dimensions();
        const mm = manifest?.mmPerUnit || 1;
        viewerRef.current.mmPerUnit = mm;
        setDims(
          size
            ? {
                x: Math.round(size.x * mm),
                y: Math.round(size.y * mm),
                z: Math.round(size.z * mm),
              }
            : null
        );
        setTree(nodeTree || []);
        setPartCount(parts.length);
      })
      .catch((err) => alive && toast.error(apiError(err) || err.message))
      .finally(() => alive && setLoading(false));
    return () => {
      alive = false;
    };
  }, [projectId, revisionId, activeDefinition, manifest]);

  const step = (delta) => {
    const all = [...(manifest?.definitions || [])].sort((a, b) =>
      a.name.localeCompare(b.name)
    );
    const index = all.findIndex((d) => d.name === activeName);
    const next = all[(index + delta + all.length) % all.length];
    if (next) setActiveName(next.name);
  };

  const toggleNode = (path) =>
    setHidden((prev) => {
      const next = new Set(prev);
      if (next.has(path)) next.delete(path);
      else next.add(path);
      viewerRef.current?.setVisibility([...next]);
      return next;
    });

  const showAll = () => {
    setHidden(new Set());
    viewerRef.current?.setVisibility([]);
  };

  const hideAll = () => {
    const paths = collectPaths(tree);
    setHidden(new Set(paths));
    viewerRef.current?.setVisibility(paths);
  };

  const isolate = () => {
    if (!activeNode) return;
    const paths = collectPaths(tree).filter(
      (path) => path !== activeNode && !activeNode.startsWith(`${path}/`)
    );
    setHidden(new Set(paths));
    viewerRef.current?.setVisibility(paths);
  };

  const selectNode = (path) => {
    setActiveNode(path);
    viewerRef.current?.highlight(path);
  };

  const applyMode = (next) => {
    setMode(next);
    viewerRef.current?.setMode(next);
  };

  useEffect(() => {
    viewerRef.current?.setMeasureSettings(measureSettings);
    localStorage.setItem("okn-measure-settings-v1", JSON.stringify(measureSettings));
  }, [measureSettings]);

  const applySection = (mode, pos) => {
    const viewer = viewerRef.current;
    if (!viewer) return;
    if (!mode) viewer.clearSection();
    else if (mode === "box") viewer.setSectionBox(1 - pos * 0.9);
    else viewer.setSectionPlane(mode);
  };

  const toggleSection = () => {
    const next = sectionMode ? null : "z";
    setSectionMode(next);
    applySection(next, sectionPos);
  };

  const changeSectionMode = (mode) => {
    setSectionMode(mode);
    applySection(mode, sectionPos);
  };

  const changeSectionPos = (pos) => {
    setSectionPos(pos);
    if (sectionMode === "box") viewerRef.current?.setSectionBox(1 - pos * 0.9);
    else if (sectionMode) viewerRef.current?.setSectionPosition(pos);
  };

  const clearSection = () => {
    setSectionMode(null);
    applySection(null, sectionPos);
  };

  // Panel collapse/expand changes the flex layout — re-fit the viewer canvas.
  useEffect(() => {
    const timer = setTimeout(() => {
      viewerRef.current?.resize();
      viewerRef.current?.fit(300);
    }, 60);
    return () => clearTimeout(timer);
  }, [leftOpen, rightOpen]);

  const nestedTotal = useMemo(() => countNodes(tree), [tree]);
  const instances = counts[activeName] ?? 0;

  if (error)
    return (
      <main className="flex flex-1 items-center justify-center p-6" data-testid="panel-properties-page">
        <p className="rounded-xl border border-slate-200 bg-white px-5 py-4 text-sm text-slate-500">
          {error}
        </p>
      </main>
    );

  if (!revisionId)
    return (
      <main className="flex flex-1 items-center justify-center p-6" data-testid="panel-properties-page">
        <p className="rounded-xl border border-slate-200 bg-white px-5 py-4 text-sm text-slate-500">
          No active model revision. Upload a Rhino model first.
        </p>
      </main>
    );

  return (
    <main
      className="flex min-h-0 flex-1 flex-col bg-[#EEF2F8]"
      data-testid="panel-properties-page"
    >
      <div className="flex flex-wrap items-center gap-2.5 border-b border-slate-200 bg-[#EEF2F8] px-3 py-2">
        <Blocks size={16} className="text-cyan-600" />
        <h2
          className="font-display text-base font-medium tracking-tight"
          style={{ color: "#0F172A" }}
        >
          Project Panels
        </h2>
        <span className="mono rounded bg-slate-900/5 px-2 py-0.5 text-[9.5px] uppercase tracking-wider text-slate-500">
          {manifest?.definitions?.length || 0} definitions
        </span>
        <button
          type="button"
          data-testid="qr-bulk-open"
          onClick={() => setQrBulkOpen(true)}
          className="mono ml-auto flex h-7 items-center gap-1.5 rounded-full border border-slate-300 bg-white px-3 text-[9.5px] uppercase tracking-wider text-slate-600 transition-colors hover:border-cyan-500/60 hover:text-cyan-700"
        >
          QR Download
        </button>
      </div>

      <BulkQrDialog
        open={qrBulkOpen}
        onOpenChange={setQrBulkOpen}
        items={panelQrItems}
        projectCode={project?.code || projectId}
      />

      <div className="flex min-h-0 flex-1 flex-col lg:flex-row">
        {leftOpen ? (
          <aside
            data-testid="definition-list-panel"
            className="relative flex min-h-0 w-full shrink-0 flex-col border-b border-slate-300 bg-[#E2E8F0] max-lg:max-h-[38vh] lg:w-72 lg:border-b-0 lg:border-r"
          >
            <button
              data-testid="toggle-left-panel"
              onClick={() => setLeftOpen(false)}
              title="Collapse definition list"
              className="absolute right-2 top-2 z-20 flex h-10 w-6 items-center justify-center rounded-full border border-slate-300 bg-white text-slate-500 shadow-lg transition-colors hover:text-cyan-700 lg:-right-3 lg:top-1/2 lg:-translate-y-1/2"
            >
              <ChevronsLeft size={12} />
            </button>
            <div className="space-y-2 border-b border-slate-300 p-2.5">
              <div className="relative">
                <Search
                  size={12}
                  className="absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-500"
                />
                <Input
                  data-testid="definition-search-input"
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                  placeholder="Search panel type…"
                  className="h-8 border-slate-300 bg-white pl-7 text-[11px] text-slate-800 placeholder:text-slate-500"
                />
              </div>
              <div className="flex gap-1" data-testid="definition-category-bar">
                {[
                  ["panels", "Panels"],
                  ["anchors", "Anchors"],
                  ["others", "Others"],
                ].map(([id, label]) => (
                  <button
                    key={id}
                    type="button"
                    data-testid={`definition-category-${id}`}
                    onClick={() => selectCategory(id)}
                    className={`mono flex h-7 flex-1 items-center justify-center rounded-full border text-[9.5px] uppercase tracking-wider transition-colors ${
                      defCategory === id
                        ? "border-cyan-500/60 bg-cyan-600/10 text-cyan-700"
                        : "border-slate-300 text-slate-600 hover:border-cyan-400/60 hover:text-cyan-700"
                    }`}
                  >
                    {label}
                  </button>
                ))}
              </div>
              <div className="flex gap-1.5">
                <button
                  data-testid="definition-prev"
                  onClick={() => step(-1)}
                  className="mono flex h-7 flex-1 items-center justify-center gap-1 rounded-full border border-slate-300 text-[9.5px] uppercase tracking-wider text-slate-600 transition-colors hover:border-cyan-400/60 hover:text-cyan-700"
                >
                  <ChevronLeft size={11} /> Prev
                </button>
                <button
                  data-testid="definition-next"
                  onClick={() => step(1)}
                  className="mono flex h-7 flex-1 items-center justify-center gap-1 rounded-full border border-slate-300 text-[9.5px] uppercase tracking-wider text-slate-600 transition-colors hover:border-cyan-400/60 hover:text-cyan-700"
                >
                  Next <ChevronRight size={11} />
                </button>
              </div>
            </div>
            <ul
              className="okn-scroll min-h-0 flex-1 overflow-y-auto p-1.5"
              data-testid="definition-list"
            >
              {definitions.map((definition) => {
                const active = definition.name === activeName;
                return (
                  <li key={definition.name}>
                    <button
                      type="button"
                      data-testid={`definition-item-${definition.name}`}
                      onClick={() => setActiveName(definition.name)}
                      className={`w-full rounded-lg px-2.5 py-2 text-left transition-colors ${
                        active
                          ? "bg-cyan-600/10 ring-1 ring-cyan-500/50"
                          : "hover:bg-slate-900/5"
                      }`}
                    >
                      <p
                        className={`mono truncate text-[12px] ${
                          active ? "text-cyan-800" : "text-slate-800"
                        }`}
                      >
                        {definition.name}
                      </p>
                      <p className="mono mt-0.5 text-[9.5px] uppercase tracking-wider text-slate-500">
                        {counts[definition.name] ?? 0} panels · {definition.parts.length} parts
                      </p>
                    </button>
                  </li>
                );
              })}
              {definitions.length === 0 ? (
                <li className="px-2.5 py-3 text-[11.5px] text-slate-500">
                  No block definition matches this category or search.
                </li>
              ) : null}
            </ul>
          </aside>
        ) : (
          <div
            data-testid="left-panel-collapsed"
            className="relative flex shrink-0 items-center justify-center border-b border-slate-300 bg-[#E2E8F0] py-1.5 lg:w-7 lg:border-b-0 lg:border-r lg:py-0"
          >
            <button
              data-testid="toggle-left-panel"
              onClick={() => setLeftOpen(true)}
              title="Expand definition list"
              className="flex h-6 w-10 items-center justify-center rounded-full border border-slate-300 bg-white text-slate-500 transition-colors hover:text-cyan-700 lg:absolute lg:-right-3 lg:top-1/2 lg:h-10 lg:w-6 lg:-translate-y-1/2"
            >
              <ChevronsRight size={12} />
            </button>
          </div>
        )}

        <section className="flex min-h-0 flex-1 flex-col">
          <InspectorToolbar
            mode={mode}
            setMode={applyMode}
            onFit={() => viewerRef.current?.fit()}
            onReset={() => {
              viewerRef.current?.reset();
              setHidden(new Set());
              setActiveNode(null);
              setSectionMode(null);
              setExplode(0);
              applyMode("orbit");
            }}
            onView={(name) => viewerRef.current?.setView(name)}
            sectionMode={sectionMode}
            sectionPos={sectionPos}
            onSectionToggle={toggleSection}
            onSectionMode={changeSectionMode}
            onSectionPos={changeSectionPos}
            onSectionClear={clearSection}
            measureTool={measureTool}
            onMeasureTool={(tool) => {
              setMeasureTool(tool);
              viewerRef.current?.setMeasureTool(tool);
            }}
            measureState={measureState}
            onMeasureDelete={() => viewerRef.current?.deleteSelectedMeasure()}
            onMeasureClear={() => viewerRef.current?.clearAllMeasures()}
            measureSettings={measureSettings}
            onMeasureSettings={setMeasureSettings}
            opacity={opacity}
            setOpacity={(value) => {
              setOpacity(value);
              viewerRef.current?.setOpacity(value / 100);
            }}
            explode={explode}
            setExplode={(value) => {
              setExplode(value);
              viewerRef.current?.setExplode((value / 100) * 1.25);
            }}
          />
          <div className="relative min-h-[220px] flex-1 lg:min-h-[320px]">
            <div
              ref={containerRef}
              className="absolute inset-0"
              data-testid="inspector-canvas"
            />
            {loading ? (
              <div className="pointer-events-none absolute inset-0 grid place-items-center bg-[#EEF2F8]/60">
                <Loader2 size={22} className="animate-spin text-cyan-600" />
              </div>
            ) : null}
          </div>
          <div
            data-testid="inspector-meta"
            className="flex flex-wrap items-center gap-x-4 gap-y-1 border-t border-slate-300 bg-[#E2E8F0] px-3 py-2"
          >
            <span className="mono text-[12px] text-cyan-800" data-testid="inspector-active-name">
              {activeName || "—"}
            </span>
            {dims ? (
              <span className="mono text-[10.5px] text-slate-500" data-testid="inspector-dims">
                {dims.x} × {dims.y} × {dims.z} mm
              </span>
            ) : null}
            <span className="mono flex items-center gap-1 text-[10.5px] text-slate-500">
              <Layers3 size={11} /> {instances} instances
            </span>
            <span className="mono text-[10.5px] text-slate-500" data-testid="inspector-parts">
              {partCount} parts · {nestedTotal} nested blocks
            </span>
          </div>
        </section>

        {rightOpen ? (
          <aside
            data-testid="nested-panel"
            className="relative flex min-h-0 w-full shrink-0 flex-col gap-3 border-t border-slate-300 bg-[#E2E8F0] p-2.5 max-lg:max-h-[34vh] lg:w-80 lg:border-l lg:border-t-0"
          >
            <button
              data-testid="toggle-right-panel"
              onClick={() => setRightOpen(false)}
              title="Collapse nested block panel"
              className="absolute right-2 top-2 z-20 flex h-10 w-6 items-center justify-center rounded-full border border-slate-300 bg-white text-slate-500 shadow-lg transition-colors hover:text-cyan-700 lg:-left-3 lg:right-auto lg:top-1/2 lg:-translate-y-1/2"
            >
              <ChevronsRight size={12} />
            </button>
            <div className="flex min-h-0 flex-1 flex-col">
              <p className="mono mb-2 text-[9.5px] uppercase tracking-[0.2em] text-slate-500">
                Nested Blocks
              </p>
              <NestedBlockTree
                tree={tree}
                hidden={hidden}
                activeNode={activeNode}
                onToggle={toggleNode}
                onSelect={selectNode}
                onShowAll={showAll}
                onHideAll={hideAll}
                onIsolate={isolate}
              />
            </div>
            {activeName && categoryOf(activeName) === "panels" ? (
              <BlockQrPanel
                projectCode={project?.code || projectId}
                name={activeName}
                count={instances}
              />
            ) : null}
          </aside>
        ) : (
          <div
            data-testid="right-panel-collapsed"
            className="relative flex shrink-0 items-center justify-center border-t border-slate-300 bg-[#E2E8F0] py-1.5 lg:w-7 lg:border-l lg:border-t-0 lg:py-0"
          >
            <button
              data-testid="toggle-right-panel"
              onClick={() => setRightOpen(true)}
              title="Expand nested block panel"
              className="flex h-6 w-10 items-center justify-center rounded-full border border-slate-300 bg-white text-slate-500 transition-colors hover:text-cyan-700 lg:absolute lg:-left-3 lg:top-1/2 lg:h-10 lg:w-6 lg:-translate-y-1/2"
            >
              <ChevronsLeft size={12} />
            </button>
          </div>
        )}
      </div>
    </main>
  );
}
